import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, Clock, CreditCard, MailX, ExternalLink, Copy, ChevronDown, ChevronUp, ShoppingCart, Send, Lock } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { getOrderLogs, fetchOrderStatuses, sendCartEmail, getWooApiUrl, WooOrderLog } from '../lib/woocommerce';

export function MobileLogs() {
  const [logs, setLogs] = useState<WooOrderLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const saved = sessionStorage.getItem('mobile_logs_auth');
    if (saved === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadLogs();
    }
  }, [isAuthenticated]);

  const handleAuth = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Recep2025' || password === 'lotis2025') {
      setIsAuthenticated(true);
      sessionStorage.setItem('mobile_logs_auth', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Mot de passe incorrect');
    }
  };

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      await fetchOrderStatuses();
    } catch (e) {
      console.warn('Erreur fetch statuts:', e);
    }
    const data = await getOrderLogs();
    setLogs(data);
    setIsLoading(false);
  };

  const isPaid = (log: WooOrderLog) => {
    return log.orderStatus === 'processing' || log.orderStatus === 'completed';
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'paid') return isPaid(log);
    if (filter === 'unpaid') return !isPaid(log);
    return true;
  });

  const paidCount = logs.filter(l => isPaid(l)).length;
  const unpaidCount = logs.filter(l => !isPaid(l)).length;

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusInfo = (log: WooOrderLog) => {
    if (log.status === 'error') return { label: 'Erreur', color: 'bg-red-100 text-red-700', icon: <MailX className="w-3.5 h-3.5" /> };
    if (!log.orderId) return { label: 'En attente', color: 'bg-gray-100 text-gray-700', icon: <Clock className="w-3.5 h-3.5" /> };
    const s = log.orderStatus;
    if (s === 'processing' || s === 'completed') return { label: 'Payé', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3.5 h-3.5" /> };
    if (s === 'on-hold') return { label: 'En attente', color: 'bg-blue-100 text-blue-700', icon: <Clock className="w-3.5 h-3.5" /> };
    if (s === 'cancelled' || s === 'refunded' || s === 'failed') return { label: s === 'cancelled' ? 'Annulé' : s === 'refunded' ? 'Remboursé' : 'Échoué', color: 'bg-red-100 text-red-700', icon: <MailX className="w-3.5 h-3.5" /> };
    return { label: 'Pas payé', color: 'bg-yellow-100 text-yellow-700', icon: <CreditCard className="w-3.5 h-3.5" /> };
  };

  const handleRelance = async (log: WooOrderLog) => {
    if (!log.customerEmail || !log.paymentUrl) {
      toast.error('Email ou lien manquant');
      return;
    }
    const toastId = toast.loading('Envoi...');
    try {
      await sendCartEmail(log.customerEmail, log.customerName || '', log.paymentUrl, log.orderTotal || '', log.clientNumber || '');
      toast.success(`Email envoyé à ${log.customerEmail}`, { id: toastId, duration: 3000 });
    } catch (e: any) {
      toast.error(`Erreur: ${e.message}`, { id: toastId });
    }
  };

  const copyMessage = (log: WooOrderLog) => {
    const link = log.paymentUrl || 'https://www.lotismode.com/valider-un-panier-live/';
    const message = `Cher client(e),\n\nVotre panier est prêt sur notre site internet.\n\nNuméro : ${log.clientNumber || ''}\nMontant : ${log.orderTotal || '0'} €\nLien : ${link}\n\nCordialement,\nL'équipe de Lotis`;
    navigator.clipboard.writeText(message).then(() => {
      toast.success('Message copié !', { duration: 1500 });
    }).catch(() => {
      toast.error('Erreur copie');
    });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800 flex items-center justify-center p-6">
        <Toaster position="top-center" />
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <ShoppingCart className="w-12 h-12 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white">LOTIS MODE</h1>
            <p className="text-purple-200 text-sm mt-1">Suivi des paniers</p>
          </div>
          <form onSubmit={handleAuth} className="bg-white rounded-2xl p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">Accès sécurisé</span>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
              placeholder="Mot de passe"
              className={`w-full px-4 py-3 rounded-xl border ${passwordError ? 'border-red-400' : 'border-gray-200'} text-base focus:outline-none focus:ring-2 focus:ring-purple-400`}
              autoFocus
            />
            {passwordError && <p className="text-red-500 text-xs mt-1">{passwordError}</p>}
            <button
              type="submit"
              className="w-full mt-4 py-3 bg-purple-600 text-white rounded-xl font-semibold text-base active:bg-purple-700"
            >
              Accéder
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" toastOptions={{ style: { fontSize: '14px' } }} />
      
      {/* Header fixe */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-purple-600" />
            <h1 className="text-lg font-bold text-gray-900">Paniers</h1>
          </div>
          <button
            onClick={loadLogs}
            disabled={isLoading}
            className="p-2 rounded-full bg-purple-50 text-purple-600 active:bg-purple-100"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="px-4 pb-3 flex gap-2">
          <div className="flex-1 bg-green-50 rounded-xl p-2.5 text-center">
            <div className="text-xl font-bold text-green-700">{paidCount}</div>
            <div className="text-[10px] text-green-600 font-medium">Payés</div>
          </div>
          <div className="flex-1 bg-yellow-50 rounded-xl p-2.5 text-center">
            <div className="text-xl font-bold text-yellow-700">{unpaidCount}</div>
            <div className="text-[10px] text-yellow-600 font-medium">En cours</div>
          </div>
          <div className="flex-1 bg-purple-50 rounded-xl p-2.5 text-center">
            <div className="text-xl font-bold text-purple-700">{logs.length}</div>
            <div className="text-[10px] text-purple-600 font-medium">Total</div>
          </div>
        </div>

        {/* Filtres */}
        <div className="px-4 pb-3 flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2 rounded-full text-xs font-semibold ${filter === 'all' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Tous
          </button>
          <button
            onClick={() => setFilter('paid')}
            className={`flex-1 py-2 rounded-full text-xs font-semibold ${filter === 'paid' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Payés
          </button>
          <button
            onClick={() => setFilter('unpaid')}
            className={`flex-1 py-2 rounded-full text-xs font-semibold ${filter === 'unpaid' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            En cours
          </button>
        </div>
      </div>

      {/* Liste des logs */}
      <div className="px-4 py-3 space-y-2 pb-20">
        {isLoading ? (
          <div className="text-center py-16">
            <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Chargement...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Aucun panier</p>
          </div>
        ) : (
          filteredLogs.map((log, idx) => {
            const status = getStatusInfo(log);
            const isExpanded = expandedId === idx;
            return (
              <div
                key={idx}
                className={`bg-white rounded-xl border overflow-hidden ${isPaid(log) ? 'border-green-200' : 'border-gray-200'}`}
              >
                {/* Ligne principale - cliquable */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : idx)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left active:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900 truncate">{log.clientNumber}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 ${status.color}`}>
                        {status.icon}
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400">{formatDate(log.timestamp)} {formatTime(log.timestamp)}</span>
                      {log.customerName && <span className="text-xs text-gray-500 truncate">· {log.customerName}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-sm text-gray-900">{log.orderTotal ? `${log.orderTotal} €` : '-'}</div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 ml-auto" /> : <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />}
                  </div>
                </button>

                {/* Détails expandés */}
                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-gray-100">
                    <div className="py-2 space-y-1.5">
                      {log.customerEmail && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Email</span>
                          <span className="text-blue-600 font-medium">{log.customerEmail}</span>
                        </div>
                      )}
                      {log.orderId && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Commande</span>
                          <span className="text-gray-700 font-medium">#{log.orderId}</span>
                        </div>
                      )}
                      {log.wooProductId && (
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">Produit WC</span>
                          <span className="text-gray-700 font-medium">#{log.wooProductId}</span>
                        </div>
                      )}
                      {log.errorMessage && (
                        <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2 mt-1">{log.errorMessage}</div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-2">
                      {isPaid(log) && log.orderId ? (
                        <a
                          href={`${getWooApiUrl()}/wp-admin/post.php?post=${log.orderId}&action=edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 py-2.5 bg-green-100 text-green-700 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-green-200"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Voir commande
                        </a>
                      ) : (
                        <>
                          {log.customerEmail && log.paymentUrl && (
                            <button
                              onClick={() => handleRelance(log)}
                              className="flex-1 py-2.5 bg-orange-100 text-orange-700 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-orange-200"
                            >
                              <Send className="w-3.5 h-3.5" />
                              Relance
                            </button>
                          )}
                          <button
                            onClick={() => copyMessage(log)}
                            className="flex-1 py-2.5 bg-yellow-100 text-yellow-700 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-yellow-200"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            Message
                          </button>
                          {log.paymentUrl && (
                            <a
                              href={log.paymentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="py-2.5 px-3 bg-purple-100 text-purple-700 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-purple-200"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
